import * as THREE from 'three';
import type { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { MeshBVH, acceleratedRaycast } from 'three-mesh-bvh';
import { m } from './site';

// Exact collision against the real facades needs spatial subdivision. Without
// it a ray has to test every triangle of whatever mesh its bounding sphere
// touches, and a building block is 16k triangles with a sphere covering the
// whole courtyard: measured at ~12 ms per step, the entire 60 fps frame budget
// spent deciding whether one foot may move. A BVH answers the same query in
// microseconds. Bounding boxes were tried first and cannot work here, the
// blocks enclose the courtyard, so their boxes contain the pool the walk is
// supposed to start at.
THREE.Mesh.prototype.raycast = acceleratedRaycast;

// First-person walk-through of the masterplan. The visitor starts at the
// courtyard pool and walks at eye height, with the buildings, walls, props and
// the water itself acting as real blockers.
//
// Everything here is authored in METRES and converted through m() at use, so
// the whole rig follows sceneScale rather than the maquette's arbitrary units:
// change SITE_SPAN_METERS and the walker stays 1.70 m tall relative to the
// buildings instead of turning into an ant or a giant.
const EYE_HEIGHT_M = 1.7;
const WALK_SPEED_MS = 2.1;
const RUN_SPEED_MS = 4.2;
// Shoulder room. Probed at two heights so a knee-high planter stops you and a
// balcony soffit overhead does not.
const BODY_RADIUS_M = 0.4;
const KNEE_M = 0.35;
// Tallest thing the walker steps up without being blocked (a stair riser).
const STEP_UP_M = 0.45;
// How far below the feet the ground may fall away before a step is refused:
// this is what keeps the visitor from strolling off the edge of the maquette.
const STEP_DOWN_M = 0.6;
const LOOK_SENSITIVITY = 0.0022;
const PITCH_LIMIT = Math.PI / 2 - 0.08;

// Groups in the GLB that a body cannot pass through. `Water` is included on
// purpose: the pool is scenery to walk around, not a surface to walk across.
// What a body cannot pass through: the building blocks and the water.
// `Elements` and `PoolElements` are deliberately absent, they are the planting,
// loungers and parasols. A lavender bush is something you brush past, and their
// mesh bounds cover the whole pool deck, so treating them as solid would fence
// the visitor out of the one place this walk exists to show.
const BLOCKER_NODES = ['Building_A', 'Building_B', 'Building_C', 'Water'];
// Probe heights above the feet for the two collision rays.
const CHEST_M = 1.25;
// Surfaces the feet can rest on.
const GROUND_NODES = ['Base'];

export interface WalkRig {
    readonly active: boolean;
    /** Returns false when the scene has no walkable ground yet. */
    enter(): boolean;
    exit(): void;
    /** Touch/on-screen input: -1..1 on each axis. */
    setMoveInput(forward: number, strafe: number): void;
    update(dt: number): void;
    dispose(): void;
}

export function createWalkMode(opts: {
    camera: THREE.PerspectiveCamera;
    controls: OrbitControls;
    domElement: HTMLElement;
    getModel: () => THREE.Group | null;
    onExit?: () => void;
}): WalkRig {
    const { camera, controls, domElement, getModel } = opts;

    let active = false;
    let yaw = 0;
    let pitch = 0;
    const keys = new Set<string>();
    let touchForward = 0;
    let touchStrafe = 0;
    let running = false;

    // Restored verbatim on exit so leaving walk mode puts the orbit view back
    // exactly where the visitor left it.
    const savedPosition = new THREE.Vector3();
    const savedTarget = new THREE.Vector3();

    const raycaster = new THREE.Raycaster();
    // Collision only asks "is anything there", so stop at the first hit rather
    // than gathering and sorting every intersection along the ray.
    (raycaster as THREE.Raycaster & { firstHitOnly?: boolean }).firstHitOnly = true;

    // Collision candidates are kept as flat mesh lists with their world-space
    // bounding boxes cached alongside. The broad phase below then hands the
    // raycaster only the two or three meshes actually within arm's reach:
    // testing every blocker every frame means ~60k triangles four times a frame
    // for a body that can only touch one wall at a time. The model never moves,
    // so the boxes stay valid for the whole walk.
    interface Candidate {
        mesh: THREE.Mesh;
        box: THREE.Box3;
    }
    type BvhGeometry = THREE.BufferGeometry & { boundsTree?: MeshBVH };
    let groundTargets: Candidate[] = [];
    let blockerTargets: Candidate[] = [];

    const collectFrom = (roots: THREE.Object3D[]): Candidate[] => {
        const out: Candidate[] = [];
        for (const root of roots) {
            root.updateWorldMatrix(true, true);
            root.traverse((child) => {
                const mesh = child as THREE.Mesh;
                if (!mesh.isMesh || !mesh.geometry) return;
                // Built once per mesh and cached on the geometry, so re-entering
                // the walk is free.
                const geometry = mesh.geometry as BvhGeometry;
                if (!geometry.boundsTree) geometry.boundsTree = new MeshBVH(geometry);
                out.push({ mesh, box: new THREE.Box3().setFromObject(mesh) });
            });
        }
        return out;
    };

    const collectTargets = () => {
        const model = getModel();
        groundTargets = [];
        blockerTargets = [];
        if (!model) return;
        const ground: THREE.Object3D[] = [];
        const blockers: THREE.Object3D[] = [];
        model.traverse((child) => {
            if (GROUND_NODES.includes(child.name)) ground.push(child);
            if (BLOCKER_NODES.includes(child.name)) blockers.push(child);
        });
        // A model whose groups were renamed would leave the walker with nothing
        // to stand on; the whole model is a coarse but working fallback.
        groundTargets = collectFrom(ground.length ? ground : [model]);
        blockerTargets = collectFrom(blockers);
    };

    // Broad phase: meshes whose world box comes within `pad` of the point.
    const probe = new THREE.Vector3();
    const near = (list: Candidate[], x: number, z: number, pad: number): THREE.Mesh[] => {
        const hits: THREE.Mesh[] = [];
        for (const c of list) {
            if (x < c.box.min.x - pad || x > c.box.max.x + pad) continue;
            if (z < c.box.min.z - pad || z > c.box.max.z + pad) continue;
            hits.push(c.mesh);
        }
        return hits;
    };

    const DOWN = new THREE.Vector3(0, -1, 0);

    /** World y of the walkable surface under x/z, or null if there is none. */
    const groundAt = (x: number, z: number, fromY: number): number | null => {
        const candidates = near(groundTargets, x, z, 0);
        if (!candidates.length) return null;
        probe.set(x, fromY, z);
        raycaster.set(probe, DOWN);
        raycaster.far = m(60);
        const hit = raycaster.intersectObjects(candidates, false)[0];
        return hit ? hit.point.y : null;
    };

    /**
     * True when something solid stands within arm's reach along `dir`.
     *
     * Probed at knee and chest height so a planter stops you and a soffit
     * overhead does not, and BVH-accelerated so both probes together cost
     * microseconds rather than milliseconds.
     */
    const blocked = (from: THREE.Vector3, dir: THREE.Vector3, feetY: number): boolean => {
        const reach = m(BODY_RADIUS_M);
        const candidates = near(blockerTargets, from.x, from.z, reach);
        if (!candidates.length) return false;
        raycaster.far = reach;
        for (const h of [KNEE_M, CHEST_M]) {
            probe.set(from.x, feetY + m(h), from.z);
            raycaster.set(probe, dir);
            if (raycaster.intersectObjects(candidates, false).length) return true;
        }
        return false;
    };

    // Poolside spawn: the pool's own bounding box gives the water's edge, then
    // step outward along its shorter axis and drop the feet onto the Base.
    const spawnAtPool = (): boolean => {
        const model = getModel();
        if (!model) return false;
        let pool: THREE.Object3D | null = null;
        model.traverse((child) => {
            if (!pool && (child.name === 'Water' || child.name === 'PoolMosaic')) pool = child;
        });
        const box = new THREE.Box3();
        if (pool) box.setFromObject(pool);
        else box.setFromObject(model);
        const centre = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());

        // Walk out to the long side of the pool, far enough to clear the water.
        const outward = size.x > size.z ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(1, 0, 0);
        const step = (outward.z ? size.z : size.x) / 2 + m(2.5);
        const spot = centre.clone().addScaledVector(outward, step);

        // Probe from just above the water rather than from high overhead, for
        // the same reason the walk loop does: anything cantilevered above the
        // pool would otherwise be mistaken for the deck.
        const y = groundAt(spot.x, spot.z, centre.y + m(3));
        if (y === null) return false;
        camera.position.set(spot.x, y + m(EYE_HEIGHT_M), spot.z);
        // Face back across the water: the pool is the view worth opening on.
        const look = centre.clone().sub(camera.position);
        yaw = Math.atan2(look.x, look.z);
        pitch = 0;
        return true;
    };

    const applyLook = () => {
        const dir = new THREE.Vector3(
            Math.sin(yaw) * Math.cos(pitch),
            Math.sin(pitch),
            Math.cos(yaw) * Math.cos(pitch),
        );
        camera.lookAt(camera.position.clone().add(dir));
    };

    const onKeyDown = (e: KeyboardEvent) => {
        if (!active) return;
        if (e.key === 'Escape') {
            exit();
            return;
        }
        keys.add(e.key.toLowerCase());
        running = e.shiftKey;
        // Arrows and space would otherwise scroll the page under the canvas.
        if (e.key.startsWith('Arrow') || e.key === ' ') e.preventDefault();
    };
    const onKeyUp = (e: KeyboardEvent) => {
        keys.delete(e.key.toLowerCase());
        running = e.shiftKey;
    };

    // Mouse look works two ways on purpose. Pointer lock is the good one, but a
    // browser can refuse it (an iframe without allow="pointer-lock", a
    // permissions policy, a user who dismissed the prompt), and without a
    // fallback that leaves the visitor first-person and unable to turn their
    // head. So when there is no lock, holding the button and dragging looks.
    let dragging = false;
    const locked = () => document.pointerLockElement === domElement;

    const onMouseDown = () => {
        if (active && !locked()) dragging = true;
    };
    const onMouseUp = () => {
        dragging = false;
    };
    const onMouseMove = (e: MouseEvent) => {
        if (!active) return;
        if (!locked() && !dragging) return;
        yaw -= e.movementX * LOOK_SENSITIVITY;
        pitch = THREE.MathUtils.clamp(pitch - e.movementY * LOOK_SENSITIVITY, -PITCH_LIMIT, PITCH_LIMIT);
    };

    // Touch look: one finger anywhere drags the view. The move stick lives in
    // the HUD and reports through setMoveInput.
    let lastTouch: { x: number; y: number } | null = null;
    const onTouchStart = (e: TouchEvent) => {
        if (!active) return;
        const t = e.touches[0];
        lastTouch = { x: t.clientX, y: t.clientY };
    };
    const onTouchMove = (e: TouchEvent) => {
        if (!active || !lastTouch) return;
        const t = e.touches[0];
        yaw -= (t.clientX - lastTouch.x) * LOOK_SENSITIVITY * 1.6;
        pitch = THREE.MathUtils.clamp(
            pitch - (t.clientY - lastTouch.y) * LOOK_SENSITIVITY * 1.6,
            -PITCH_LIMIT,
            PITCH_LIMIT,
        );
        lastTouch = { x: t.clientX, y: t.clientY };
    };
    const onTouchEnd = () => {
        lastTouch = null;
    };

    // Losing the pointer lock (Esc, tab-out, browser UI) must leave walk mode
    // too, otherwise the visitor is stuck first-person with no way to look.
    // Only meaningful once a lock has actually been held: without this guard a
    // refused lock request would read as "lock lost" and bounce the visitor
    // straight back out of the walk.
    let hadLock = false;
    const onPointerLockChange = () => {
        if (!active) return;
        if (locked()) hadLock = true;
        else if (hadLock) exit();
    };

    function enter(): boolean {
        if (active) return true;
        collectTargets();
        savedPosition.copy(camera.position);
        savedTarget.copy(controls.target);
        if (!spawnAtPool()) return false;

        active = true;
        controls.enabled = false;
        applyLook();

        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('keyup', onKeyUp);
        document.addEventListener('mousemove', onMouseMove);
        domElement.addEventListener('mousedown', onMouseDown);
        window.addEventListener('mouseup', onMouseUp);
        document.addEventListener('pointerlockchange', onPointerLockChange);
        domElement.addEventListener('touchstart', onTouchStart, { passive: true });
        domElement.addEventListener('touchmove', onTouchMove, { passive: true });
        domElement.addEventListener('touchend', onTouchEnd);
        // Pointer lock needs a user gesture; entering from a dock click is one.
        // A refusal is not an error worth surfacing: drag-look covers it.
        if (!('ontouchstart' in window)) {
            Promise.resolve(domElement.requestPointerLock?.()).catch(() => {});
        }
        return true;
    }

    function exit(): void {
        if (!active) return;
        active = false;
        keys.clear();
        touchForward = 0;
        touchStrafe = 0;
        hadLock = false;
        dragging = false;
        window.removeEventListener('keydown', onKeyDown);
        window.removeEventListener('keyup', onKeyUp);
        document.removeEventListener('mousemove', onMouseMove);
        domElement.removeEventListener('mousedown', onMouseDown);
        window.removeEventListener('mouseup', onMouseUp);
        document.removeEventListener('pointerlockchange', onPointerLockChange);
        domElement.removeEventListener('touchstart', onTouchStart);
        domElement.removeEventListener('touchmove', onTouchMove);
        domElement.removeEventListener('touchend', onTouchEnd);
        if (document.pointerLockElement === domElement) document.exitPointerLock();

        camera.position.copy(savedPosition);
        controls.target.copy(savedTarget);
        controls.enabled = true;
        controls.update();
        opts.onExit?.();
    }

    function update(dt: number): void {
        if (!active) return;

        const forward =
            (keys.has('w') || keys.has('arrowup') ? 1 : 0) -
            (keys.has('s') || keys.has('arrowdown') ? 1 : 0) +
            touchForward;
        const strafe =
            (keys.has('d') || keys.has('arrowright') ? 1 : 0) -
            (keys.has('a') || keys.has('arrowleft') ? 1 : 0) +
            touchStrafe;

        applyLook();
        if (!forward && !strafe) return;

        const speed = m(running ? RUN_SPEED_MS : WALK_SPEED_MS) * Math.min(dt, 0.1);
        // Movement is level even when looking up or down: pitch aims the eyes,
        // not the feet.
        const ahead = new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw));
        // right = ahead x up. The opposite winding, (ahead.z, 0, -ahead.x), is
        // up x ahead and points LEFT, which swapped A and D.
        const right = new THREE.Vector3(-ahead.z, 0, ahead.x);
        const step = ahead
            .multiplyScalar(forward)
            .addScaledVector(right, strafe)
            .setY(0);
        if (step.lengthSq() === 0) return;
        step.normalize();

        // Axes are resolved separately so a body sliding along a wall keeps the
        // component that is still free instead of stopping dead.
        for (const axis of [
            new THREE.Vector3(step.x, 0, 0),
            new THREE.Vector3(0, 0, step.z),
        ]) {
            if (axis.lengthSq() === 0) continue;
            const feet = camera.position.y - m(EYE_HEIGHT_M);
            if (blocked(camera.position, axis.clone().normalize(), feet)) continue;
            const nx = camera.position.x + axis.x * speed;
            const nz = camera.position.z + axis.z * speed;

            // Ground is still raycast: step height has to follow the real
            // paving, and Base is a couple of thousand triangles, not sixty.
            //
            // The probe starts just above the FEET, not above the head. Starting
            // at eye level meant a first-floor terrace overhead was the first
            // thing the downward ray met, so every step next to one was scored
            // as a 1.8 m climb and refused: the visitor was cemented in place a
            // single stride from the pool.
            const ground = groundAt(nx, nz, feet + m(STEP_UP_M));
            if (ground === null) continue;
            const rise = ground - feet;
            if (rise > m(STEP_UP_M) || rise < -m(STEP_DOWN_M)) continue;
            camera.position.set(nx, ground + m(EYE_HEIGHT_M), nz);
        }
        applyLook();
    }

    return {
        get active() {
            return active;
        },
        enter,
        exit,
        setMoveInput(f, s) {
            touchForward = THREE.MathUtils.clamp(f, -1, 1);
            touchStrafe = THREE.MathUtils.clamp(s, -1, 1);
        },
        update,
        dispose() {
            exit();
        },
    };
}
